import * as v from 'valibot'

const Count = v.pipe(v.number(), v.integer(), v.minValue(0))
// 作品内容 id 的写法（引擎 id()）：人物与表演状态都是它。
const ContentId = v.pipe(v.string(), v.regex(/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,79}$/))
const Milliseconds = v.pipe(v.number(), v.minValue(0), v.maxValue(86_400_000))
const ReportCount = v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(1000))
// 同一回合第几次建会话（勘误 21）：从 1 起，每次重试 +1；上限只为挡住越界的整数，正常一回合只有几次。
const Attempt = v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(1000))

/**
 * 结束一场的原因（与 Demo `end` 同一集合）：browser_disconnected 与 expired 也由服务端自己使用；
 * turn_cancelled 是并行准备的那场所属回合失败或取消（勘误 22）。
 */
export const NOVEL_DRAMA_END_REASONS = Object.freeze([
  'completed',
  'cancelled_during_start',
  'disconnected',
  'start_failed',
  'playback_failed',
  'hidden',
  'switched_off',
  'unmounted',
  'turn_cancelled',
  'replaced',
  'browser_disconnected',
  'expired',
])

/**
 * 建一场数字人演出（POST /api/novel/runs/:id/drama/sessions，C29，勘误 21、24）：request_id 是回合的 request_id（流里收到 stage
 * 就可以建，勘误 22），attempt 是这一回合第几次建会话；人物须是非玩家、状态须 drama_ready（https 状态图）。
 * 按 novel_drama_session 预扣，幂等键由 run_id、request_id、attempt 与人物、状态确定；同一次尝试（run_id、request_id、attempt）
 * 再提交回 409 request_id_conflict，账本不动。
 * 失败：402 INSUFFICIENT_BALANCE（details 同回合）、409 CONFLICT drama_busy（本局已有一场）/ request_id_conflict、
 * 400 BAD_REQUEST drama_unavailable / drama_scene_unavailable、502 drama_start_failed（已退款，错误码在 code 上）。
 * @typedef {{ request_id: string, attempt: number, character_id: string, state_id: string }} NovelDramaSessionBodyT
 */
export const NovelDramaSessionBody = v.object({
  provider: v.optional(v.picklist(['auto', 'vidu', 'vivix']), 'auto'),
  location_id: v.optional(ContentId),
  request_id: v.pipe(v.string(), v.uuid()),
  attempt: Attempt,
  character_id: ContentId,
  state_id: ContentId,
})

/**
 * 建好的一场：live_id 与 conn_id 用于控制连接的 conn_init，avatar_uid 是频道里数字人的用户 id，viewer_token 是观众入会的 ARTC Token；
 * ws_path 是本服务的控制连接地址（带一次性 token，C32）。Vidu 与 ARTC 的密钥不下发。
 */
export const NovelDramaSession = v.pipe(v.object({
  provider: v.optional(v.picklist(['vidu', 'vivix']), 'vidu'),
  control: v.optional(v.record(v.string(), v.unknown())),
  delivery: v.optional(v.record(v.string(), v.unknown())),
  aspect_ratio: v.optional(v.string()),
  session_id: v.pipe(v.string(), v.uuid()),
  live_id: v.pipe(v.string(), v.minLength(1)),
  conn_id: v.pipe(v.string(), v.uuid()),
  ws_path: v.pipe(v.string(), v.regex(/^\/ws\/novel\/drama\/[0-9a-f-]{36}\?token=[A-Za-z0-9_-]+$/)),
  avatar_uid: v.optional(v.pipe(v.string(), v.minLength(1))),
  viewer_token: v.optional(v.pipe(v.string(), v.minLength(1))),
  charged_points: Count,
}), v.check(session => session.provider === 'vivix'
  ? Boolean(session.control?.url && session.delivery?.media)
  : Boolean(session.avatar_uid && session.viewer_token), 'missing provider connection'))

/** 未提交演出内容前切换供应商；live_id 防止旧连接事件覆盖已经切换的新尝试。 */
export const NovelDramaSessionRetryBody = v.strictObject({
  failed_provider: v.picklist(['vidu', 'vivix']),
  live_id: v.pipe(v.string(), v.minLength(1)),
  reason: v.literal('connect_failed'),
})

/** 实际媒体首帧报告；服务端还须验证当前供应商尝试及控制连接已经就绪。 */
export const NovelDramaDeliveredBody = v.strictObject({
  provider: v.picklist(['vidu', 'vivix']),
  live_id: v.pipe(v.string(), v.minLength(1)),
})
export const NovelDramaDelivered = v.object({ delivered: v.literal(true) })

/** 会话人物对白段配音（POST …/drama/sessions/:session_id/speech，C30）：只收活动分支上该人物的对白段。 */
export const NovelDramaSpeechBody = v.object({
  segment_id: v.pipe(v.string(), v.minLength(1), v.maxLength(60)),
})

/** 配音结果：整段音频 base64（格式同对白配音），由玩家端转成 24 kHz 单声道 16 bit PCM 推给数字人。 */
export const NovelDramaSpeech = v.object({
  segment_id: v.string(),
  text: v.string(),
  audio_base64: v.pipe(v.string(), v.minLength(1)),
})

/** 玩家端的演出诊断摘要（推流节奏、收尾依据、实测出声）：一场一份的扁平数值，只用于排查，不参与计费。 */
const NovelDramaPlaybackDiagnostics = v.pipe(
  v.record(v.pipe(v.string(), v.regex(/^[a-z][a-z0-9_]{0,47}$/)), v.nullable(v.union([v.pipe(v.number(), v.finite()), v.pipe(v.string(), v.maxLength(48)), v.boolean()]))),
  v.check(value => Object.keys(value).length <= 32, 'too many playback diagnostics'),
)

/** 玩家端上报的耗时（毫秒，非整数由服务端取整）与计数；没走到的阶段传 null 或不传，计数为 null 时记 0。 */
export const NovelDramaMetrics = v.object({
  request_to_ready_ms: v.optional(v.nullable(Milliseconds)),
  request_to_first_video_ms: v.optional(v.nullable(Milliseconds)),
  request_to_playback_ms: v.optional(v.nullable(Milliseconds)),
  request_to_disconnect_ms: v.optional(v.nullable(Milliseconds)),
  action_count: v.optional(v.nullable(ReportCount)),
  dialogue_count: v.optional(v.nullable(ReportCount)),
  playback: v.optional(v.nullable(NovelDramaPlaybackDiagnostics)),
})

/**
 * 结束一场（DELETE …/drama/sessions/:session_id）。控制、内容提交与匹配媒体报告共同确认交付前，以 start_failed、
 * playback_failed、disconnected、browser_disconnected、expired 结束的退款；turn_cancelled 不论是否交付，该回合没有提交就退款；
 * completed 与玩家主动结束的不退。不在服务端的会话（已结束、已过期）同样回 ended。
 */
export const NovelDramaEndBody = v.object({
  reason: v.picklist(NOVEL_DRAMA_END_REASONS),
  metrics: v.optional(NovelDramaMetrics),
})

export const NovelDramaEnded = v.object({ ended: v.literal(true) })

/** A lookup never creates a supplier session or charges points. Omit request_id to inspect the current stage. */
export const NovelDramaVideoLookupBody = v.strictObject({
  provider: v.picklist(['vidu', 'vivix']),
  request_id: v.optional(v.pipe(v.string(), v.uuid())),
})
export const NovelDramaVideoLookup = v.object({
  enabled: v.boolean(),
  hit: v.boolean(),
  request_id: v.nullable(v.string()),
  asset_key: v.nullable(v.pipe(v.string(), v.regex(/^[a-f0-9]{64}$/))),
})
export const NovelDramaVideoSaved = v.object({
  saved: v.literal(true),
  asset_key: v.pipe(v.string(), v.regex(/^[a-f0-9]{64}$/)),
})

export const NovelAvatarPrepareBody = v.strictObject({ provider: v.picklist(['vidu', 'vivix']) })
export const NovelAvatarPublicationQuery = v.strictObject({ version_id: v.optional(v.pipe(v.string(), v.minLength(1))) })
export const NovelAvatarPublication = v.object({
  pack_key: v.string(), status: v.picklist(['ready', 'partial']),
  ready_asset_count: Count, total_asset_count: Count,
})
export const NovelAvatarPublicationStatus = v.object({
  content_hash: v.string(),
  ready: v.boolean(),
  providers: v.array(v.object({
    provider: v.picklist(['vidu', 'vivix']),
    status: v.picklist(['not_required', 'unprepared', 'preparing', 'ready', 'failed', 'unavailable']),
    ready_asset_count: Count,
    total_asset_count: Count,
    error_code: v.nullable(v.string()),
  })),
})
export const NovelDramaPlanBody = v.strictObject({
  request_id: v.pipe(v.string(), v.uuid()), character_id: ContentId, state_id: ContentId, location_id: v.optional(ContentId),
})
export const NovelDramaPlan = v.object({
  items: v.array(v.record(v.string(), v.unknown())), elapsed_ms: Count, status: v.picklist(['ready', 'fallback']),
})
